import toNumber from 'lodash/toNumber';

import { parseJsonStringArray } from './jsonUtils';
import {
  LeasePaymentFrequency,
  LeaseRemittanceType,
  ReconciliationAllocationMode,
  Setting,
  SettingType,
} from './types/model-app';

// TO UPDATE IN COMMUNICATION V2 NEXT TICKETS (remove MailSendingOption)
export enum MailSendingOption {
  AUTOMATIC = 'Automatic',
  MANUAL = 'Manual',
  DISABLED = 'Disabled',
}

export enum NotificationOption {
  DISABLED = 'Disabled',
  ENABLED = 'Enabled',
}

export enum CommunicationTimeFrame {
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
  EVENING = 'EVENING',
  NIGHT = 'NIGHT',
}

export enum InvoiceDetailLevelOptions {
  DETAILED = 'detailed',
  SUMMARY = 'summary',
}

export enum DueDateOption {
  IMMEDIATE = 'IMMEDIATE',
  FIFTEEN_DAYS = 'FIFTEEN_DAYS',
  THIRTY_DAYS = 'THIRTY_DAYS',
  END_OF_MONTH = 'END_OF_MONTH',
  CUSTOM_DATE = 'CUSTOM_DATE',
}

export const NOT_APPLICABLE = 'N/A';
const getUTCHourFromCET = (CETHour: number) => CETHour - 1;

export const COMMUNICATION_TIME_FRAME_START = {
  // UTC Hours, increasing order
  [CommunicationTimeFrame.MORNING]: getUTCHourFromCET(9),
  [CommunicationTimeFrame.AFTERNOON]: getUTCHourFromCET(12),
  [CommunicationTimeFrame.EVENING]: getUTCHourFromCET(17),
  [CommunicationTimeFrame.NIGHT]: getUTCHourFromCET(20),
};
export const getCommunicationTimeFrameStart = (communicationTimeFrame: CommunicationTimeFrame) => {
  switch (communicationTimeFrame) {
    case CommunicationTimeFrame.MORNING: {
      return getUTCHourFromCET(9);
    }
    case CommunicationTimeFrame.AFTERNOON: {
      return getUTCHourFromCET(12);
    }
    case CommunicationTimeFrame.EVENING: {
      return getUTCHourFromCET(17);
    }
    case CommunicationTimeFrame.NIGHT:
    default: {
      return getUTCHourFromCET(20);
    }
  }
};

export const DEFAULT_MF_INVOICE_SCHEDULE_CREATE_DAY = '20';
export const DEFAULT_MF_INVOICE_SCHEDULE_POST_DAY = '28';

export const DEFAULT_LEASE_STATEMENT_PERIOD = 12;
export const DEFAULT_OWNER_STATEMENT_PERIOD = 1;
export const DEFAULT_RECONCILIATION_OVERPAYMENT_CAP_MONTHS = 2;
export const DEFAULT_FEE_ARREARS_ALERT_DAYS = 30;
export const MAX_FEE_ARREARS_ALERT_DAYS = 365;
export const DEFAULT_RESIDENTIAL_LEASE_REMITTANCE_TYPE = LeaseRemittanceType.STRUCTURED_FIXED;
export const DEFAULT_COMMERCIAL_LEASE_REMITTANCE_TYPE = LeaseRemittanceType.STRUCTURED_BY_INVOICE;

export const SETTING_DEFAULTS = {
  [SettingType.ADVANCED_LEASE_CHARGES_INVOICE]: false,
  [SettingType.AUTOMATIC_INDEXATION]: true,
  [SettingType.CHARGE_ADJUSTMENT_PERCENTAGE_VARIATION]: '15',
  [SettingType.COMMUNICATION_TIME_FRAME]: [
    CommunicationTimeFrame.MORNING,
    CommunicationTimeFrame.AFTERNOON,
    CommunicationTimeFrame.EVENING,
  ],
  [SettingType.MF_ENABLED]: false,
  [SettingType.MF_START_FISCAL_YEAR]: undefined,
  [SettingType.MF_END_FISCAL_YEAR]: undefined,
  [SettingType.MF_DEFAULT_VAT_RATE_ID]: undefined,
  [SettingType.MF_DEFAULT_LEGAL_CONTACT_ID]: undefined,
  [SettingType.MF_INVOICE_DETAIL_LEVEL]: InvoiceDetailLevelOptions.DETAILED,
  [SettingType.MF_IBAN_FOR_MANAGEMENT_FEES]: undefined,
  [SettingType.MF_INVOICE_SCHEDULE_FREQUENCY]: LeasePaymentFrequency.MONTHLY,
  [SettingType.MF_INVOICE_SCHEDULE_CREATE_DATE]: DEFAULT_MF_INVOICE_SCHEDULE_CREATE_DAY,
  [SettingType.MF_INVOICE_SCHEDULE_POST_DATE]: DEFAULT_MF_INVOICE_SCHEDULE_POST_DAY,
  [SettingType.MF_INVOICE_SCHEDULE_ENABLED]: false,
  [SettingType.MF_INVOICE_SCHEDULE_AUTO_SEND]: false,
  [SettingType.MF_INVOICE_SCHEDULE_DUE_DATE]: DueDateOption.THIRTY_DAYS,
  [SettingType.MF_DEFAULT_SEND_CHANNELS]: '["EMAIL"]',
  [SettingType.SHOW_INVOICE_SENDER_ADDRESS_WHEN_AGENCY]: false,
  [SettingType.SHOW_BUILDING_SITE]: false,
  [SettingType.AGENCY_RATES_ENABLED]: false,
  [SettingType.OWNER_STATEMENTS_ENABLED]: false,
  [SettingType.STATEMENT_SUPPORTING_DOCUMENTS_ATTACHED]: false,
  [SettingType.RECONCILIATION_ALLOCATION_MODE]: ReconciliationAllocationMode.AUTOMATIC,
  [SettingType.RECONCILIATION_OVERPAYMENT_CAP_MONTHS]: DEFAULT_RECONCILIATION_OVERPAYMENT_CAP_MONTHS,
  [SettingType.RECONCILIATION_DRAFT_EARLY_POSTING]: false,
  [SettingType.PAYMENTS_DOUBLE_CHECKS_ENABLED]: true,
  [SettingType.PAYMENTS_FEE_ARREARS_ALERT_DAYS]: DEFAULT_FEE_ARREARS_ALERT_DAYS,
  [SettingType.TICKET_NOTIFICATION_ENABLED]: false,
  [SettingType.TICKET_NOTIFICATION_RECIPIENTS]: '[]',
  [SettingType.LEASE_REMITTANCE_TYPE_RESIDENTIAL]: DEFAULT_RESIDENTIAL_LEASE_REMITTANCE_TYPE,
  [SettingType.LEASE_REMITTANCE_TYPE_COMMERCIAL]: DEFAULT_COMMERCIAL_LEASE_REMITTANCE_TYPE,
};

export const getSettingValueOrDefault = (settingType: SettingType, settings?: Setting[]) => {
  const setting = getSettingObjectIfExist(settingType, settings);
  if (setting) {
    return setting.value;
  }
  return String(SETTING_DEFAULTS[settingType]);
};

export const getSettingObjectIfExist = (settingType: SettingType, settings?: Setting[]) =>
  settings?.find((setting) => setting.name === settingType) ?? null;

export const getSettingValueForAdvancedChargesInvoices = (settings?: Setting[]) =>
  getSettingValueForBoolean(SettingType.ADVANCED_LEASE_CHARGES_INVOICE, settings);

export const getSettingValueForAutomaticIndexation = (settings?: Setting[]) =>
  getSettingValueForBoolean(SettingType.AUTOMATIC_INDEXATION, settings);

export const getSettingValueForDraftEarlyPosting = (settings?: Setting[]) =>
  getSettingValueForBoolean(SettingType.RECONCILIATION_DRAFT_EARLY_POSTING, settings);

export const getSettingValueForBoolean = (settingType: SettingType, settings?: Setting[]) => {
  const value = getSettingValueOrDefault(settingType, settings);
  return value === 'true' || value === '1';
};

export const getSettingValueForTicketNotificationRecipients = (settings?: Setting[]): string[] =>
  parseJsonStringArray(getSettingValueOrDefault(SettingType.TICKET_NOTIFICATION_RECIPIENTS, settings));

export const getSettingValueForChargeAdjustmentPercentageVariation = (settings?: Setting[]) =>
  toNumber(getSettingValueOrDefault(SettingType.CHARGE_ADJUSTMENT_PERCENTAGE_VARIATION, settings));

export const NO_ENABLED_TIMEFRAME = 'NO_ENABLED_TIMEFRAME';
export const getSettingValueForCommunicationTimeFrame = (settings?: Setting[]) => {
  const stringValue = getSettingValueOrDefault(SettingType.COMMUNICATION_TIME_FRAME, settings);
  const listValue = (stringValue !== NO_ENABLED_TIMEFRAME ? stringValue.split(',') : []) as CommunicationTimeFrame[];
  return listValue;
};

export const parsePositiveIntegerSetting = (
  value: string | null | undefined,
  maximum: number = Number.MAX_SAFE_INTEGER
): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= maximum ? parsed : undefined;
};

export const getOverpaymentCapMonths = (settings?: Setting[]): number =>
  parsePositiveIntegerSetting(getSettingValueOrDefault(SettingType.RECONCILIATION_OVERPAYMENT_CAP_MONTHS, settings)) ??
  DEFAULT_RECONCILIATION_OVERPAYMENT_CAP_MONTHS;

export const getFeeArrearsAlertDays = (settings?: Setting[]): number =>
  parsePositiveIntegerSetting(
    getSettingValueOrDefault(SettingType.PAYMENTS_FEE_ARREARS_ALERT_DAYS, settings),
    MAX_FEE_ARREARS_ALERT_DAYS
  ) ?? DEFAULT_FEE_ARREARS_ALERT_DAYS;
